import React, { useContext, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { App as CapApp } from '@capacitor/app';
import { Keyboard } from '@capacitor/keyboard';
import { StatusBar, Style } from '@capacitor/status-bar';
import { isNativeApp, isNativeIOS } from './utils/platform.utils';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, AuthContext } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { LocationProvider } from './contexts/LocationContext';
import { Header } from './components/layout/Header';
import { PageVisitTracker } from './components/common/PageVisitTracker';
import { BottomNav } from './components/layout/BottomNav';
import { Login } from './pages/Login';
import { Home } from './pages/Home';
import { Profile } from './pages/Profile';
import { VacationTime } from './pages/VacationTime';
import { InstallPage } from './pages/InstallPage';
import { ScrollToTop } from './components/common/ScrollToTop';
import { ClockInOut }  from './components/modules/ClockInOut';
import { ManualTimeEntry } from './components/modules/ManualTimeEntry';
import { JobsList } from './components/modules/JobsList';
import { TruckDriverJobsList } from './components/modules/TruckDriverJobsList';
import { JobDetails } from './components/modules/JobDetails';
import { TruckTickets } from './components/modules/TruckTickets';
import TMTickets from './components/modules/TMTickets';
import { TimeManagement } from './components/modules/TimeManagement';
import { TimeCardEditor } from './components/modules/TimeCardEditor';
import { MyTime } from './components/modules/MyTime';
import { EmployeeManagement } from './components/modules/EmployeeManagement';
import { ClockedIn } from './components/modules/ClockedIn';
import { JobManagement } from './components/modules/JobManagement';
import DailyReports from './components/modules/DailyReports';
import AsphaltReports from './components/modules/AsphaltReports';
import { AdminDailyReports } from './components/modules/AdminDailyReports';
import { AdminAsphaltReports } from './components/modules/AdminAsphaltReports';
import PhotoGallery from './components/modules/PhotoGallery';
import UploadStatus from './components/modules/UploadStatus';
import { PlansViewer } from './components/modules/PlansViewer';
import { EquipmentIssues } from './components/modules/EquipmentIssues';
import { MechanicsIssues } from './components/modules/MechanicsIssues';
import MachineHub from './components/modules/MachineHub';
import EquipmentInspection from './components/modules/EquipmentInspection';
import { EquipmentInspectionEnhanced } from './components/modules/EquipmentInspectionEnhanced';
import LinkTracker from './components/modules/LinkTracker';
import ManageTrackers from './components/modules/ManageTrackers';
import FleetView from './components/modules/FleetView';
import TrucksLowboys from './components/modules/TrucksLowboys';
import { KioskTodoAdmin } from './components/modules/KioskTodoAdmin';
import { canAccessPage } from './config/gatedPages';
import { OpenInspections } from './components/modules/OpenInspections';
import ServiceLog from './components/modules/ServiceLog';
import Inventory from './components/modules/Inventory';
import EquipmentDispatch from './components/modules/EquipmentDispatch';
import TruckSchedule from './components/modules/TruckSchedule';
import Receipts from './components/modules/Receipts';
import { TruckerTracking } from './components/modules/TruckerTracking';
import { TruckerDay } from './components/modules/TruckerDay';
import { Bidding } from './components/modules/Bidding';
import { BiddingDetail } from './components/modules/BiddingDetail';
import { JobActivity } from './components/modules/JobActivity';
import { DailySummaryViewer } from './components/modules/DailySummaryViewer';
import { TruckTicketViewer } from './components/modules/TruckTicketViewer';
import { DailyReportViewer } from './components/modules/DailyReportViewer';
import { TMTicketViewer } from './components/modules/TMTicketViewer';
import { StartupSheetViewer } from './components/modules/StartupSheetViewer';
import { SubcontractorViewer } from './components/modules/SubcontractorViewer';
import { RemoteSign } from './components/modules/RemoteSign';
import SpecialRequirements from './components/modules/SpecialRequirements';
import JobSubcontractors from './components/modules/JobSubcontractors';
import JobScopeOfWork from './components/modules/JobScopeOfWork';
import { ModulePlaceholder } from './components/common/ModulePlaceholder';
import { syncService } from './services/sync.service';
import { uploadQueueService } from './services/upload-queue.service';
import { navigationBarService } from './services/navigationBar.service';
import { InstallPrompt } from './components/InstallPrompt';
import { PWAUpdater } from './components/PWAUpdater';
import { UpdateBanner } from './components/UpdateBanner';
import { ForceUpdateModal } from './components/ForceUpdateModal';
import { GlobalMismatchChecker } from './components/GlobalMismatchChecker';
import { appVersionService, UpdateCheckResult } from './services/appVersion.service';
import { useRegisterSW } from 'virtual:pwa-register/react';
import './styles/global.css';

const queryClient = new QueryClient();

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const auth = useContext(AuthContext);

  if (auth?.isLoading) {
    return <div className="loading">Loading...</div>;
  }

  if (!auth?.user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const auth = useContext(AuthContext);

  if (auth?.isLoading) {
    return <div className="loading">Loading...</div>;
  }

  if (!auth?.user) {
    return <Navigate to="/login" replace />;
  }

  // Only admin and foreman roles can access admin routes
  const allowedRoles = ['admin', 'foreman'];
  if (!allowedRoles.includes(auth.user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Gated page guard — only super admins or users granted the page key (see
// config/gatedPages). Hand-typed URLs can't bypass the hidden dashboard button.
function PageAccessRoute({ pageKey, children }: { pageKey: string; children: React.ReactNode }) {
  const auth = useContext(AuthContext);
  if (auth?.isLoading) return <div className="loading">Loading...</div>;
  if (!auth?.user) return <Navigate to="/login" replace />;
  if (!canAccessPage(auth.user, pageKey)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AppContent() {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [onlineStatus, setOnlineStatus] = useState(navigator.onLine);
  const [updateCheck, setUpdateCheck] = useState<UpdateCheckResult | null>(null);

  // Check app version on launch (native apps only)
  useEffect(() => {
    const checkAppVersion = async () => {
      const result = await appVersionService.checkVersion();
      if (result && (result.updateRequired || result.maintenanceMode)) {
        setUpdateCheck(result);
      }
    };
    checkAppVersion();
  }, []);

  // Handle opening app store for update
  const handleOpenAppStore = () => {
    if (updateCheck?.storeUrl) {
      window.open(updateCheck.storeUrl, '_system');
    }
  };

  // Handle Android hardware back button
  useEffect(() => {
    if (!isNativeApp()) return;

    const backButtonListener = CapApp.addListener('backButton', ({ canGoBack }) => {
      // Root/login → minimize (act like Android home), don't exit.
      if (location.pathname === '/' || location.pathname === '/login') {
        CapApp.minimizeApp();
      } else if (canGoBack) {
        // Pop one entry — the page's own popstate guard then closes its open layer
        // (drawer → map → list) or navigate('/')s home. Stays inside the app.
        window.history.back();
      } else {
        // No webview history to pop → go to the dashboard. The old code called
        // history.back() here (via the `history.length > 1` branch) even when
        // canGoBack was false, which walked off the stack and CLOSED THE APP on
        // Fleet/Trucks. navigate('/') never exits.
        navigate('/');
      }
    });

    return () => {
      backButtonListener.then(listener => listener.remove());
    };
  }, [location.pathname, navigate]);

  useEffect(() => {
    // Start upload queue service app-wide (so uploads continue even when navigating away)
    uploadQueueService.startAutoSync();

    // Set Android navigation bar to black (native app only)
    navigationBarService.setDarkTheme();

    // iOS StatusBar: Config in capacitor.config.ts handles overlaysWebView: true
    // CSS env(safe-area-inset-top) on .header handles safe area padding
    // Style.Dark = WHITE text/icons (for our dark/black header background)
    if (isNativeIOS()) {
      StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
      // Don't call setOverlaysWebView at runtime - let config handle it
    }

    // Start T&M ticket sync (handles initial sync and online event listening)
    syncService.startTMTicketSync();

    // Online/Offline handling
    const handleOnline = () => {
      setOnlineStatus(true);
      syncService.syncPendingActions();
      syncService.syncPendingTMTickets(); // Sync pending T&M tickets when back online
      uploadQueueService.startAutoSync(); // Resume uploads when back online
    };

    const handleOffline = () => {
      setOnlineStatus(false);
    };

    // Handle app returning from background
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('[App] Returned from background, restarting upload sync');
        uploadQueueService.startAutoSync(); // Fix stuck uploads and resume
        syncService.syncPendingTMTickets(); // Sync any queued T&M tickets

        // iOS: Reset StatusBar style after background (style can get reset)
        // Don't touch overlaysWebView - config handles that
        if (isNativeIOS()) {
          try {
            await StatusBar.setStyle({ style: Style.Dark }); // White text for dark header
            console.log('[App] StatusBar style reset after resume');
          } catch (e) {
            console.log('[App] StatusBar style reset failed:', e);
          }
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Prevent undo keyboard shortcuts (Cmd+Z on iOS/Mac, Ctrl+Z on Windows)
    // This prevents iOS shake-to-undo from affecting form inputs
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      // Also prevent Cmd+Shift+Z (redo)
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'z') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // Prevent beforeinput undo/redo events
    const handleBeforeInput = (e: Event) => {
      const inputEvent = e as InputEvent;
      if (inputEvent.inputType === 'historyUndo' || inputEvent.inputType === 'historyRedo') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // Reset zoom when app regains focus (after returning from Maps, etc)
    const handleFocus = () => {
      // Force reset viewport scale
      const viewport = document.querySelector('meta[name=viewport]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
      }
    };

    // Prevent Android context menu on long press (shows app icon "B")
    const handleContextMenu = (e: MouseEvent) => {
      // Allow context menu on inputs for paste functionality
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        return;
      }
      e.preventDefault();
      return false;
    };

    // Scroll focused input into view when keyboard opens (for adjustNothing mode)
    // Only scroll within .main-content container, not the whole page
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        // Small delay to let keyboard open first
        setTimeout(() => {
          // Find the scrollable main-content container
          const mainContent = document.querySelector('.main-content');
          if (mainContent) {
            // Get target position relative to main-content
            const targetRect = target.getBoundingClientRect();
            const containerRect = mainContent.getBoundingClientRect();

            // Calculate scroll position to center the input in visible area
            // Account for keyboard taking roughly half the screen
            const visibleHeight = window.innerHeight * 0.4; // Assume keyboard takes 60%
            const targetCenter = targetRect.top - containerRect.top + mainContent.scrollTop;
            const scrollTo = targetCenter - (visibleHeight / 2);

            mainContent.scrollTo({
              top: Math.max(0, scrollTo),
              behavior: 'smooth'
            });
          }
        }, 300);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('beforeinput', handleBeforeInput, true);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('focusin', handleFocusIn);

    // Dynamic keyboard padding (native apps only)
    // Adds .keyboard-open class to main-content when keyboard is visible
    let keyboardShowListener: any = null;
    let keyboardHideListener: any = null;

    if (isNativeApp()) {
      Keyboard.addListener('keyboardWillShow', (info) => {
        // Set CSS variable with actual keyboard height
        document.documentElement.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
        // Add class to body so both main-content and bottom-nav can respond
        document.body.classList.add('keyboard-open');
      }).then(listener => { keyboardShowListener = listener; });

      Keyboard.addListener('keyboardWillHide', () => {
        document.body.classList.remove('keyboard-open');
      }).then(listener => { keyboardHideListener = listener; });
    }

    // Listen for service worker messages for sync
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', event => {
        if (event.data && event.data.type === 'SYNC_PENDING') {
          syncService.syncPendingActions();
        }
        if (event.data && event.data.type === 'CHECK_TIME_ENTRIES') {
          console.log('Checking time entries...');
        }
      });
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('beforeinput', handleBeforeInput, true);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('focusin', handleFocusIn);
      // Clean up keyboard listeners
      if (keyboardShowListener) keyboardShowListener.remove();
      if (keyboardHideListener) keyboardHideListener.remove();
    };
  }, []);

  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/install" element={<InstallPage />} />
        {/* Public route for remote T&M ticket signing */}
        <Route path="/sign-ticket/:token" element={<RemoteSign />} />
        <Route path="/*" element={
          <ProtectedRoute>
            <>
              <Header onlineStatus={onlineStatus} />
              <PageVisitTracker />
              <main className="main-content">
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/vacation-time" element={<VacationTime />} />
                  <Route path="/clock-in" element={<ClockInOut />} />
                  <Route path="/clock-out" element={<ClockInOut />} />
                  <Route path="/time" element={<ClockInOut />} />
                  <Route path="/time-clock" element={<ClockInOut />} />
                  <Route path="/clock" element={<ManualTimeEntry />} />
                  {/* Self-gated to super admin inside the component (testing); open up later for all employees */}
                  <Route path="/my-time" element={<MyTime />} />

                  {/* Jobs module */}
                  <Route path="/jobs" element={<JobsList />} />
                  <Route path="/jobs/:id" element={<JobDetails />} />
                  <Route path="/cdl-jobs" element={<TruckDriverJobsList />} />

                  {/* Truck Tickets module */}
                  <Route path="/trucks" element={<TruckTickets />} />

                  {/* T&M Tickets module */}
                  <Route path="/tm-tickets" element={<TMTickets />} />

                  {/* Time Management module (Admin/Foreman only) */}
                  <Route path="/time-management" element={<AdminRoute><TimeCardEditor mode="manage" /></AdminRoute>} />
                  <Route path="/time-management-classic" element={<AdminRoute><TimeManagement /></AdminRoute>} />
                  <Route path="/approve-timecards" element={<AdminRoute><TimeCardEditor /></AdminRoute>} />

                  {/* Employee Management module (Admin/Foreman only) */}
                  <Route path="/employee-management" element={<AdminRoute><EmployeeManagement /></AdminRoute>} />

                  {/* Clocked In Employees (Admin only) */}
                  <Route path="/clocked-in" element={<AdminRoute><ClockedIn /></AdminRoute>} />

                  {/* Job Management module (Admin/Foreman only) */}
                  <Route path="/job-management" element={<AdminRoute><JobManagement /></AdminRoute>} />

                  {/* Add placeholder routes for other modules */}
                  <Route path="/tickets/new" element={
                    <ModulePlaceholder
                      moduleName="New Ticket"
                      description="Submit a new field ticket"
                      endpoint="/api/tickets"
                    />
                  } />
                  <Route path="/daily-reports" element={<DailyReports />} />
                  <Route path="/asphalt-reports" element={<AsphaltReports />} />
                  <Route path="/admin-asphalt-reports" element={<AdminRoute><AdminAsphaltReports /></AdminRoute>} />
                  <Route path="/view-daily-reports" element={<AdminRoute><AdminDailyReports /></AdminRoute>} />
                  <Route path="/photos" element={<PhotoGallery />} />
                  <Route path="/upload-status" element={<UploadStatus />} />
                  <Route path="/plans" element={<PlansViewer />} />

                  {/* Equipment Issues module */}
                  <Route path="/equipment" element={<EquipmentIssues />} />
                  <Route path="/equipment/issues" element={<EquipmentIssues />} />
                  <Route path="/link-tracker" element={<LinkTracker />} />
                  <Route path="/manage-trackers" element={<ManageTrackers />} />
                  <Route path="/fleet" element={<AdminRoute><FleetView /></AdminRoute>} />
                  <Route path="/truck-fleet" element={<PageAccessRoute pageKey="trucks"><TrucksLowboys /></PageAccessRoute>} />
                  <Route path="/shop-todo" element={<PageAccessRoute pageKey="shop-todo"><KioskTodoAdmin /></PageAccessRoute>} />

                  {/* Mechanics Issues module */}
                  <Route path="/mechanics/issues" element={<MechanicsIssues />} />

                  {/* Machine Hub module */}
                  <Route path="/mechanics/machine-hub" element={<MachineHub />} />

                  {/* Service Log module */}
                  <Route path="/service-log" element={<ServiceLog />} />

                  {/* Equipment Inspection module */}
                  <Route path="/equipment/inspection/:equipmentId" element={<EquipmentInspection />} />

                  {/* Enhanced Equipment Inspection (after migration) */}
                  <Route path="/mechanics/inspection/:equipmentId" element={<EquipmentInspectionEnhanced />} />

                  {/* Open Inspections */}
                  <Route path="/mechanics/open-inspections" element={<OpenInspections />} />

                  {/* Equipment Dispatch */}
                  <Route path="/dispatch" element={<AdminRoute><EquipmentDispatch /></AdminRoute>} />
                  {/* Truck Schedule builder */}
                  <Route path="/truck-schedule" element={<AdminRoute><TruckSchedule /></AdminRoute>} />

                  {/* Receipts */}
                  <Route path="/receipts" element={<Receipts />} />

                  {/* Inventory */}
                  <Route path="/inventory" element={<Inventory />} />

                  {/* Trucker Tracking */}
                  <Route path="/trucker-tracking" element={<TruckerTracking />} />

                  {/* Trucker Day v2 (GPS-first clock-in, TEST MODE — direct link only) */}
                  <Route path="/trucker-day" element={<TruckerDay />} />

                  {/* Bidding (admin-only Bid Room view) */}
                  <Route path="/bidding" element={<Bidding />} />
                  <Route path="/bidding/:id" element={<BiddingDetail />} />

                  {/* Job Activity (Admin/Foreman only) */}
                  <Route path="/job-activity" element={<AdminRoute><JobActivity /></AdminRoute>} />

                  {/* Daily Summary Viewer (Admin only) */}
                  <Route path="/daily-summary" element={<AdminRoute><DailySummaryViewer /></AdminRoute>} />

                  {/* Truck Ticket Viewer (Admin only) */}
                  <Route path="/truck-ticket-viewer" element={<AdminRoute><TruckTicketViewer /></AdminRoute>} />

                  {/* Daily Report Viewer (Admin only) */}
                  <Route path="/daily-report-viewer" element={<AdminRoute><DailyReportViewer /></AdminRoute>} />

                  {/* T&M Ticket Viewer (Admin only) */}
                  <Route path="/tm-ticket-viewer" element={<AdminRoute><TMTicketViewer /></AdminRoute>} />

                  {/* Startup Sheet Viewer (Admin only) */}
                  <Route path="/startup-sheet" element={<AdminRoute><StartupSheetViewer /></AdminRoute>} />

                  {/* Subcontractor Viewer (Admin only) */}
                  <Route path="/subcontractor-viewer" element={<AdminRoute><SubcontractorViewer /></AdminRoute>} />

                  {/* Job Subcontractors (Admin only) */}
                  <Route path="/job-subcontractors" element={<AdminRoute><JobSubcontractors /></AdminRoute>} />

                  {/* Scope of Work (Admin only) */}
                  <Route path="/job-scope-of-work" element={<AdminRoute><JobScopeOfWork /></AdminRoute>} />

                  {/* Special Requirements (shown after clock-in for admin/foreman) */}
                  <Route path="/special-requirements" element={<SpecialRequirements />} />

                  <Route path="/safety" element={
                    <ModulePlaceholder 
                      moduleName="Safety"
                      description="Report safety incidents and near misses"
                      endpoint="/api/safety"
                    />
                  } />
                  <Route path="/shop-time" element={
                    <ModulePlaceholder
                      moduleName="Shop Time"
                      description="Track shop time"
                      endpoint="/api/time/shop-time"
                    />
                  } />

                  {/* PWA Management Page */}
                  <Route path="/pwa-manager" element={
                    <div style={{
                      padding: '20px',
                      maxWidth: '400px',
                      margin: '0 auto',
                      backgroundColor: '#f8fafc',
                      minHeight: '100vh'
                    }}>
                      <h1 style={{
                        fontSize: '24px',
                        fontWeight: '600',
                        color: '#1e293b',
                        marginBottom: '16px',
                        textAlign: 'center'
                      }}>
                        PWA Manager
                      </h1>
                      <p style={{
                        color: '#64748b',
                        marginBottom: '24px',
                        textAlign: 'center',
                        lineHeight: '1.5'
                      }}>
                        Update and manage your Bradanna Field App installation
                      </p>
                      <PWAUpdater />
                    </div>
                  } />
                </Routes>
              </main>
              <BottomNav />
            </>
          </ProtectedRoute>
        } />
      </Routes>

      {/* Force Update Modal - blocks app until user updates (native apps only) */}
      <ForceUpdateModal
        isOpen={updateCheck?.updateRequired || updateCheck?.maintenanceMode || false}
        message={updateCheck?.message || ''}
        storeUrl={updateCheck?.storeUrl || ''}
        isMaintenanceMode={updateCheck?.maintenanceMode}
        onUpdate={handleOpenAppStore}
      />

      {/* PWA Install Prompt - only shows when not in standalone mode */}
      <InstallPrompt />

      {/* Update Banner - only shows when update is available */}
      <UpdateBanner />

      {/* Offline indicator - consistent message across app */}
      {!onlineStatus && (
        <div style={{
          position: 'fixed',
          bottom: '90px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#f97316',
          color: 'white',
          padding: '10px 16px',
          borderRadius: '8px',
          zIndex: 9999,
          fontSize: '14px',
          fontWeight: 500,
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>📡</span>
          No internet connection. Please check your WiFi or data.
        </div>
      )}
    </>
  );
}

function App() {
  // Check if running in standalone mode
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                       (window.navigator as any).standalone || 
                       document.referrer.includes('android-app://');

  // Log PWA install status
  useEffect(() => {
    if (isStandalone) {
      console.log('App is running in standalone mode');
    } else {
      console.log('App is running in browser mode');
    }
  }, [isStandalone]);

  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <AuthProvider>
          <LocationProvider>
            <Router>
              <ScrollToTop />
              <GlobalMismatchChecker />
              <AppContent />
            </Router>
          </LocationProvider>
        </AuthProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}

export default App;